import { useEffect, useRef, useState } from 'react';
import { BRIEF_FIELD_NODE, listBriefFields, type BriefFieldSummary } from '../../../shared/briefFields';
import type { TiptapEditor } from '../Editor/Editor';

/**
 * The brief's fields, kept in step with the editor's document.
 *
 * Both the Fields drawer and the hidden-fields strip need the same list, and
 * both are mounted while you type. Reading the whole document on every
 * transaction would mean serialising it on every keystroke, so the hook does
 * the cheap thing first: it walks the document's top-level nodes (a dozen of
 * them) for a signature, and only re-reads the field list when that signature
 * actually moves. Typing inside a field changes nothing here and costs nothing.
 *
 * `active` is false while the drawer is shut, so a closed drawer holds no
 * listener at all.
 */
export function useBriefFields(
  editor: TiptapEditor | null,
  active = true,
): BriefFieldSummary[] {
  const [fields, setFields] = useState<BriefFieldSummary[]>([]);
  const signature = useRef<string | null>(null);

  useEffect(() => {
    if (!editor || !active) {
      signature.current = null;
      return;
    }
    const sync = () => {
      const next = fieldSignature(editor);
      if (next === signature.current) return;
      signature.current = next;
      setFields(listBriefFields(editor.getJSON()));
    };
    sync();
    editor.on('transaction', sync);
    return () => {
      editor.off('transaction', sync);
    };
  }, [editor, active]);

  return fields;
}

/** Everything the two views render, in document order, as one cheap string. */
function fieldSignature(editor: TiptapEditor): string {
  const parts: string[] = [];
  editor.state.doc.forEach((node) => {
    if (node.type.name !== BRIEF_FIELD_NODE) return;
    const a = node.attrs;
    parts.push(`${a.id}|${a.enabled}|${a.audience}|${a.shoot}|${a.title}`);
  });
  return parts.join('\n');
}
